import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Printer, Download } from 'lucide-react';
import { prescriptionService } from '../api/prescriptions';
import { encounterService } from '../api/encounters';
import { patientService, doctorService } from '../../../lib/services';
import { printPrescription, downloadPrescription } from '../utils/prescriptionPdf';
import type { Prescription, Encounter } from '../types';
import type { Patient, Doctor } from '../../../lib/supabase';

export default function PrescriptionPrint() {
  const { id } = useParams<{ id: string }>();
  const [prescription, setPrescription] = useState<Prescription | null>(null);
  const [encounter, setEncounter] = useState<Encounter | null>(null);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [doctor, setDoctor] = useState<Doctor | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  const loadData = async () => {
    if (!id) return;
    
    try {
      const prescriptionData = await prescriptionService.getById(id);
      setPrescription(prescriptionData);

      const encounterData = await encounterService.getById(prescriptionData.encounterId);
      setEncounter(encounterData);

      const [patientData, doctorData] = await Promise.all([
        patientService.getById(encounterData.patientId),
        doctorService.getById(encounterData.doctorId),
      ]);

      setPatient(patientData);
      setDoctor(doctorData);
    } catch (error) {
      console.error('Failed to load prescription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (prescription && encounter) {
      const encounterWithDetails = {
        ...encounter,
        patient,
        doctor,
      };
      printPrescription(prescription, encounterWithDetails);
    }
  };

  const handleDownload = () => {
    if (prescription && encounter) {
      const encounterWithDetails = {
        ...encounter,
        patient,
        doctor,
      };
      downloadPrescription(prescription, encounterWithDetails);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!prescription || !encounter) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900">Prescription not found</h2>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Print Actions */}
      <div className="flex justify-end space-x-3 mb-6 no-print">
        <button
          onClick={handleDownload}
          className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors duration-200"
        >
          <Download className="w-4 h-4" />
          <span>Download PDF</span>
        </button>
        <button
          onClick={handlePrint}
          className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200"
        >
          <Printer className="w-4 h-4" />
          <span>Print</span>
        </button>
      </div>

      {/* Prescription Print View */}
      <div className="bg-white p-8 shadow-lg print:shadow-none print:p-0" style={{ minHeight: '297mm', width: '210mm' }}>
        {/* Clinic Header */}
        <div className="text-center border-b-2 border-gray-300 pb-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-900">MEDSYSTEM CLINIC</h1>
          <p className="text-sm text-gray-600 mt-1">123 Medical Street, Healthcare City</p>
          <p className="text-sm text-gray-600">Phone: +1-555-0123 | Email: info@medsystem.com</p>
        </div>

        {/* Prescription Header */}
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">PRESCRIPTION</h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p><strong>Doctor:</strong> {prescription.signedBy || doctor?.name || 'Dr. Unknown'}</p>
              <p><strong>Date:</strong> {new Date(encounter.dateTime).toLocaleDateString()}</p>
              <p><strong>Prescription ID:</strong> {prescription.id}</p>
            </div>
            <div>
              <p><strong>Patient:</strong> {patient?.name || 'Unknown Patient'}</p>
              {patient?.email && <p><strong>Email:</strong> {patient.email}</p>}
              {patient?.phone && <p><strong>Phone:</strong> {patient.phone}</p>}
            </div>
          </div>
        </div>

        {/* Medications */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Medications:</h3>
          <table className="w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-50">
                <th className="border border-gray-300 px-3 py-2 text-left">Medicine</th>
                <th className="border border-gray-300 px-3 py-2 text-left">Dose</th>
                <th className="border border-gray-300 px-3 py-2 text-left">Frequency</th>
                <th className="border border-gray-300 px-3 py-2 text-left">Duration</th>
              </tr>
            </thead>
            <tbody>
              {prescription.items.map((item, index) => (
                <tr key={index}>
                  <td className="border border-gray-300 px-3 py-2">
                    <div>
                      <strong>{index + 1}. {item.nameOrMedicineId}</strong>
                      {item.instructions && (
                        <div className="text-sm text-gray-600 mt-1">
                          Instructions: {item.instructions}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="border border-gray-300 px-3 py-2">{item.dose}</td>
                  <td className="border border-gray-300 px-3 py-2">{item.frequency}</td>
                  <td className="border border-gray-300 px-3 py-2">{item.duration}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* General Advice */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">General Advice:</h3>
          <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
            <li>Take medications as prescribed</li>
            <li>Complete the full course of antibiotics if prescribed</li>
            <li>Follow up if symptoms persist or worsen</li>
            <li>Maintain adequate hydration and rest</li>
          </ul>
        </div>

        {/* Signature */}
        <div className="mt-12">
          <div className="flex justify-between items-end">
            <div>
              <div className="border-b border-gray-400 w-64 mb-2"></div>
              <p className="text-sm text-gray-700">Doctor Signature</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-700">
                <strong>Signed by:</strong> {prescription.signedBy || doctor?.name || 'Dr. Unknown'}
              </p>
              <p className="text-sm text-gray-700">
                <strong>Date:</strong> {new Date().toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-4 border-t border-gray-300">
          <p className="text-xs text-gray-500 text-center">
            This is a computer-generated prescription. For any queries, please contact the clinic.
          </p>
        </div>
      </div>

      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          
          body {
            margin: 0;
            padding: 0;
          }
          
          @page {
            size: A4;
            margin: 1cm;
          }
        }
      `}</style>
    </div>
  );
}
